import { useEffect, useState } from 'react'
import {
  BarChart3,
  Boxes,
  KeyRound,
  Menu,
  MessageSquare,
  Moon,
  Plug,
  Radio,
  Settings2,
  Sun,
  X,
  Zap,
} from 'lucide-react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useLocale, type Locale } from '../lib/i18n'
import { useTheme, type Theme } from './theme-provider'
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui'
import { AuthPanel } from './auth-panel'
import { InstallPrompt } from './install-prompt'

const navigation = [
  { href: '/chat', label: 'Chat', icon: <MessageSquare className="h-[17px] w-[17px]" /> },
  { href: '/', label: 'Operations', icon: <BarChart3 className="h-[17px] w-[17px]" /> },
  { href: '/models', label: 'Models', icon: <Boxes className="h-[17px] w-[17px]" /> },
  { href: '/channels', label: 'Channels', icon: <Radio className="h-[17px] w-[17px]" /> },
  { href: '/api-keys', label: 'API Keys', icon: <KeyRound className="h-[17px] w-[17px]" /> },
  { href: '/integrations', label: 'Integrations', icon: <Plug className="h-[17px] w-[17px]" /> },
  { href: '/settings', label: 'Settings', icon: <Settings2 className="h-[17px] w-[17px]" /> },
] as const

export function AppShell({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const { t, locale, setLocale } = useLocale()
  const { theme, setTheme } = useTheme()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const [auth, setAuth] = useState<'loading' | 'authenticated' | 'anonymous'>('loading')

  useEffect(() => {
    let cancelled = false
    async function checkSession() {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await fetch('/api/auth/session', { credentials: 'include', cache: 'no-store' })
          if (!cancelled) setAuth(response.ok ? 'authenticated' : 'anonymous')
          return
        } catch {
          if (attempt === 1 && !cancelled) setAuth('anonymous')
          await new Promise((resolve) => setTimeout(resolve, 250))
        }
      }
    }
    void checkSession()
    return () => { cancelled = true }
  }, [])

  if (auth === 'loading') return <div className="flex min-h-screen items-center justify-center bg-panel text-sm text-ink-muted">Connecting…</div>
  if (auth === 'anonymous') return <AuthPanel onAuthenticated={() => setAuth('authenticated')} />

  const closeMenu = () => setOpen(false)

  return (
    <div className="h-screen overflow-hidden bg-panel text-ink lg:pl-[228px]">
      {open ? <button aria-label={t.closeMenu} className="fixed inset-0 z-30 bg-black/30 backdrop-blur-[2px] lg:hidden" onClick={closeMenu} /> : null}
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-[228px] flex-col border-r border-panel-border bg-panel-muted/80 backdrop-blur-xl transition-transform lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex h-[68px] items-center justify-between px-4">
          <Link to="/chat" onClick={closeMenu} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 outline-none focus-visible:ring-2 focus-visible:ring-accent-info/50">
            <span className="flex h-7 w-7 items-center justify-center rounded-[9px] bg-ink text-panel"><Zap className="h-3.5 w-3.5" fill="currentColor" /></span>
            <span className="text-[14px] font-semibold tracking-[-0.02em] text-ink">{t.product}</span>
          </Link>
          <button className="rounded-md p-1.5 text-ink-muted hover:bg-panel-raised hover:text-ink lg:hidden" aria-label={t.closeMenu} onClick={closeMenu}><X className="h-4 w-4" /></button>
        </div>

        <div className="px-4 pb-4 pt-2">
          <div className="rounded-xl border border-panel-border/70 bg-panel-raised/60 px-3 py-2.5">
            <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-ink-faint">Workspace</div>
            <div className="mt-1 truncate text-[13px] font-medium text-ink">{t.workspace}</div>
          </div>
        </div>

        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-2" aria-label="Primary navigation">
          <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-faint">Workspace</div>
          {navigation.map((item) => <NavItem key={item.href} href={item.href} label={labelFor(item.href, t)} icon={item.icon} active={isActive(pathname, item.href)} onClick={closeMenu} />)}
        </nav>

        <div className="space-y-2 border-t border-panel-border/70 p-3">
          <div className="flex items-center gap-2 px-2 pb-1 text-xs text-ink-muted"><span className="h-2 w-2 rounded-full bg-accent-ok" />Live service</div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={locale} onValueChange={(value) => setLocale(value as Locale)}><SelectTrigger className="h-8 w-full border-transparent bg-panel-raised/60 text-[11px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="zh">中文</SelectItem></SelectContent></Select>
            <Select value={theme} onValueChange={(value) => setTheme(value as Theme)}><SelectTrigger className="h-8 w-full border-transparent bg-panel-raised/60 text-[11px]"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="system">{t.system}</SelectItem><SelectItem value="light"><span className="inline-flex items-center gap-2"><Sun className="h-3.5 w-3.5" />{t.light}</span></SelectItem><SelectItem value="dark"><span className="inline-flex items-center gap-2"><Moon className="h-3.5 w-3.5" />{t.dark}</span></SelectItem></SelectContent></Select>
          </div>
        </div>
      </aside>

      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex h-14 shrink-0 items-center border-b border-panel-border/70 bg-panel/90 px-4 backdrop-blur lg:hidden">
          <Button variant="ghost" className="mr-2 h-9 w-9 px-0" aria-label={t.menu} onClick={() => setOpen(true)}><Menu className="h-[18px] w-[18px]" /></Button>
          <span className="text-sm font-semibold text-ink">{pageTitle(pathname, t)}</span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>

      <nav className="fixed inset-x-3 bottom-3 z-20 flex h-14 items-center justify-around rounded-2xl border border-panel-border bg-panel-raised/95 px-2 shadow-panel backdrop-blur lg:hidden" aria-label="Mobile navigation">
        {navigation.slice(0, 4).map((item) => <Link key={item.href} to={item.href} onClick={closeMenu} className={`flex h-10 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl text-[10px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent-info/50 ${isActive(pathname, item.href) ? 'bg-ink text-panel' : 'text-ink-muted hover:bg-panel-muted hover:text-ink'}`} activeOptions={{ exact: item.href === '/' }}><span className="flex h-5 items-center justify-center">{item.icon}</span><span className="max-w-full truncate px-1">{labelFor(item.href, t)}</span></Link>)}
      </nav>
      <InstallPrompt />
    </div>
  )
}

function NavItem({ label, icon, active, href, onClick }: { label: string; icon: React.ReactNode; active?: boolean; href: string; onClick: () => void }) {
  return <Link to={href} onClick={onClick} activeOptions={{ exact: href === '/' }} aria-current={active ? 'page' : undefined} className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent-info/50 ${active ? 'bg-ink text-panel shadow-sm' : 'text-ink-muted hover:bg-panel-raised hover:text-ink'}`}><span className={`flex h-5 w-5 items-center justify-center ${active ? 'text-panel' : 'text-ink-faint group-hover:text-ink'}`}>{icon}</span><span className="truncate">{label}</span>{active ? <span className="ml-auto h-1.5 w-1.5 rounded-full bg-accent-info" /> : null}</Link>
}

function isActive(pathname: string, href: string) { return href === '/' ? pathname === '/' : pathname.startsWith(href) }

function labelFor(href: string, t: ReturnType<typeof useLocale>['t']) {
  return href === '/'
    ? t.operations
    : href === '/chat'
    ? t.chat
    : href === '/models'
    ? t.models
    : href === '/channels'
    ? t.channels
    : href === '/api-keys'
    ? t.apiKeys
    : href === '/integrations'
    ? t.integrations
    : t.settings
}

function pageTitle(pathname: string, t: ReturnType<typeof useLocale>['t']) {
  return pathname === '/'
    ? t.operations
    : pathname.startsWith('/chat')
    ? t.chat
    : pathname.startsWith('/models')
    ? t.models
    : pathname.startsWith('/channels')
    ? t.channels
    : pathname.startsWith('/api-keys')
    ? t.apiKeys
    : pathname.startsWith('/integrations')
    ? t.integrations
    : t.settings
}
